import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Loader2, Lock, Mail, Phone, MapPin, Check, Info, X, Plus, KeyRound, Unlink, Sparkles, RotateCcw, Send, ChevronDown, ShieldCheck } from "lucide-react";
import { SlideOver } from "@/components/ui/SlideOver";
import { TechAvatar } from "@/components/TechAvatar";
import { InfoTip } from "@/components/InfoTip";
import { supabase } from "@/lib/supabase";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson } from "@/lib/orgSettings";
import { UserPermissionsEditor } from "./UserPermissionsEditor";
import { deptRoleName } from "./deptRole";
import { cn } from "@/lib/cn";

// ============================================================================
// TECH-PAY (#131) — the technician profile drawer. Reads the mirror for
// employment/contact/schedule (ESC stays read-only, DEC-22); Pegasus-native
// for the things the mirror doesn't carry: bio (the "meet your tech" line),
// hire date, and PAY + COMMISSION. Pay feeds job-costing labor + commission
// and is gated behind team.pay — it NEVER touches the customer invoice.
// ============================================================================

interface Props {
  empno: string | null;
  name: string;
  onClose: () => void;
}

interface TechDetail {
  empno: string; inactive: boolean | null;
  dept: string | null; hire_date: string | null; pay_rate: number | null;
  email: string | null; cellphone: string | null; phone: string | null;
  add1: string | null; city: string | null; state: string | null; zip: string | null;
  mon_start: string | null; mon_end: string | null; tue_start: string | null; tue_end: string | null;
  wed_start: string | null; wed_end: string | null; thu_start: string | null; thu_end: string | null;
  fri_start: string | null; fri_end: string | null; sat_start: string | null; sat_end: string | null;
  sun_start: string | null; sun_end: string | null;
}
interface PayRow {
  // PAY-ONE-01: the one pay record (also edited on Reports → Pay Rates).
  pay_type: string; base_rate: number; burden: number | null;
  rhp_multiplier: number; phone_stipend: number;
  commission_kind: string; commission_rate: number; effective_date: string | null;
  note: string | null; updated_by: string | null; updated_at: string | null;
}
interface ProfileRow {
  bio: string | null; title: string | null; hire_date: string | null; role_id: number | null;
  email: string | null; cellphone: string | null; add1: string | null;
  city: string | null; state: string | null; zip: string | null;
}
interface TeamRole { id: number; name: string }
interface SkillChip { id: number; name: string; qualifierOverride: string | null; domainQualifier: string | null; sort: number }
interface SkillDomain { id: number; name: string; qualifier: string | null; sort: number }

const SKILL_LEVELS = ["Master", "Journeyman"];

const PAY_TYPES: [string, string][] = [["hourly", "Hourly"], ["salary", "Salary"]];
const COMMISSION_KINDS: [string, string][] = [
  ["none", "No commission"],
  ["service_invoice_pct", "Service — % of invoice total"],
  ["install_gp_pct", "Install — % of gross profit"],
];
const DAYS: [string, keyof TechDetail, keyof TechDetail][] = [
  ["Mon", "mon_start", "mon_end"], ["Tue", "tue_start", "tue_end"], ["Wed", "wed_start", "wed_end"],
  ["Thu", "thu_start", "thu_end"], ["Fri", "fri_start", "fri_end"], ["Sat", "sat_start", "sat_end"],
  ["Sun", "sun_start", "sun_end"],
];

function useTechDetail(empno: string | null) {
  return useQuery({
    queryKey: ["tech_detail", empno], enabled: !!empno,
    queryFn: async (): Promise<TechDetail | null> => {
      const { data, error } = await supabase.from("mirror_employee")
        .select("empno, inactive, dept, hire_date, pay_rate, email, cellphone, phone, add1, city, state, zip, mon_start, mon_end, tue_start, tue_end, wed_start, wed_end, thu_start, thu_end, fri_start, fri_end, sat_start, sat_end, sun_start, sun_end")
        .eq("empno", empno).maybeSingle();
      if (error) throw error;
      return (data as TechDetail) ?? null;
    },
  });
}
function useTechPay(empno: string | null) {
  return useQuery({
    queryKey: ["tech_pay", empno], enabled: !!empno,
    queryFn: async (): Promise<PayRow | null> => {
      const { data, error } = await supabase.from("tech_pay")
        .select("pay_type, base_rate, burden, rhp_multiplier, phone_stipend, commission_kind, commission_rate, effective_date, note, updated_by, updated_at")
        .eq("empno", empno).maybeSingle();
      if (error) throw error;
      return (data as PayRow) ?? null;
    },
  });
}
function useTechProfile(empno: string | null) {
  return useQuery({
    queryKey: ["tech_profile", empno], enabled: !!empno,
    queryFn: async (): Promise<ProfileRow | null> => {
      const { data, error } = await supabase.from("tech_profile")
        .select("bio, title, hire_date, role_id, email, cellphone, add1, city, state, zip")
        .eq("empno", empno).maybeSingle();
      if (error) throw error;
      return (data as ProfileRow) ?? null;
    },
  });
}
function useTeamRoles() {
  return useQuery({
    queryKey: ["team_roles"], staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<TeamRole[]> => {
      const { data, error } = await supabase.from("team_role")
        .select("id, name").eq("archived", false).order("sort");
      if (error) throw error;
      return (data ?? []) as TeamRole[];
    },
  });
}
function useTechSkills(empno: string | null) {
  return useQuery({
    queryKey: ["tech_skills_profile", empno], enabled: !!empno,
    queryFn: async (): Promise<SkillChip[]> => {
      const [tsRes, sdRes] = await Promise.all([
        supabase.from("tech_skill").select("skill_domain_id, qualifier_override").eq("empno", empno).eq("admin_flagged_capable", true),
        supabase.from("skill_domain").select("id, name, qualifier, sort_order"),
      ]);
      const dom = new Map<number, { name: string; qualifier: string | null; sort: number }>();
      for (const d of (sdRes.data ?? []) as { id: number; name: string; qualifier: string | null; sort_order: number | null }[])
        dom.set(d.id, { name: d.name, qualifier: d.qualifier, sort: d.sort_order ?? 0 });
      return ((tsRes.data ?? []) as { skill_domain_id: number; qualifier_override: string | null }[])
        .map((r) => {
          const m = dom.get(r.skill_domain_id);
          return m ? { id: r.skill_domain_id, name: m.name, qualifierOverride: r.qualifier_override, domainQualifier: m.qualifier, sort: m.sort } : null;
        })
        .filter((x): x is SkillChip => x !== null)
        .sort((a, b) => a.sort - b.sort);
    },
  });
}
function useSkillDomains() {
  return useQuery({
    queryKey: ["skill_domains"], staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SkillDomain[]> => {
      const { data, error } = await supabase.from("skill_domain")
        .select("id, name, qualifier, sort_order").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return ((data ?? []) as { id: number; name: string; qualifier: string | null; sort_order: number | null }[])
        .map((d) => ({ id: d.id, name: d.name, qualifier: d.qualifier, sort: d.sort_order ?? 0 }));
    },
  });
}

// Supabase/PostgREST errors are plain objects (not Error instances), so a bare
// String(e) yields "[object Object]". Pull .message when present.
const errText = (e: unknown): string =>
  e instanceof Error ? e.message
  : e && typeof e === "object" && "message" in e ? String((e as { message: unknown }).message)
  : String(e);

const money = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTime = (t: string | null) => {
  if (!t) return null;
  const s = t.trim();
  if (!s) return null;
  let h: number, m: string;
  if (s.includes(":")) {
    const [hh, mm] = s.split(":");
    h = parseInt(hh, 10); m = (mm ?? "00").slice(0, 2);
  } else {
    const digits = s.replace(/\D/g, "");
    if (digits.length !== 3 && digits.length !== 4) return s;
    h = parseInt(digits.slice(0, digits.length - 2), 10); m = digits.slice(-2);
  }
  if (!Number.isFinite(h)) return s;
  const ap = h >= 12 ? "p" : "a";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m}${ap}`;
};

export function TechProfileDrawer({ empno, name, onClose }: Props) {
  const open = !!empno;
  const qc = useQueryClient();
  const { can, isAdmin } = usePermissions();
  const canPay = can("team.pay");
  const canEdit = can("team.edit");
  const canManageLogins = isAdmin || can("users.manage");

  const { data: detail } = useTechDetail(empno);
  const { data: pay } = useTechPay(empno);
  const { data: profile } = useTechProfile(empno);
  const { data: roles } = useTeamRoles();

  // The role this tech defaults to from their ESC department (override ⊕ derive):
  // the dropdown preselects it, and on save we only persist role_id when the choice
  // differs from this derived default — so the office never re-keys what ESC knows.
  const derivedRoleId = (roles ?? []).find((r) => r.name === deptRoleName(detail?.dept))?.id ?? null;

  // Profile (Pegasus-native) edit state
  const [bio, setBio] = useState("");
  const [title, setTitle] = useState("");
  const [hireDate, setHireDate] = useState("");
  const [roleId, setRoleId] = useState("");
  // Contact overrides (null/blank = fall back to the ESC mirror value).
  const [email, setEmail] = useState("");
  const [cell, setCell] = useState("");
  const [add1, setAdd1] = useState("");
  const [city, setCity] = useState("");
  const [stateAbbr, setStateAbbr] = useState("");
  const [zip, setZip] = useState("");
  // Pay (Pegasus-native, gated) edit state
  const [payType, setPayType] = useState("hourly");
  const [baseRate, setBaseRate] = useState("");
  const [burden, setBurden] = useState("");
  const [rhpMult, setRhpMult] = useState("1.75");
  const [phone, setPhone] = useState("");
  const [commKind, setCommKind] = useState("none");
  const [commRate, setCommRate] = useState("");
  const [effDate, setEffDate] = useState("");
  const [payNote, setPayNote] = useState("");

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset local edit state whenever a different tech opens or data lands.
  useEffect(() => {
    setBio(profile?.bio ?? "");
    setTitle(profile?.title ?? "");
    // Hire date + role + contacts all show the EFFECTIVE value (Pegasus override,
    // else the ESC mirror / derived default). On save we persist an override only
    // when it differs from what ESC already gives us.
    setHireDate(profile?.hire_date ?? detail?.hire_date ?? "");
    setRoleId(profile?.role_id != null ? String(profile.role_id) : (derivedRoleId != null ? String(derivedRoleId) : ""));
    setEmail(profile?.email ?? detail?.email ?? "");
    setCell(profile?.cellphone ?? detail?.cellphone ?? detail?.phone ?? "");
    setAdd1(profile?.add1 ?? detail?.add1 ?? "");
    setCity(profile?.city ?? detail?.city ?? "");
    setStateAbbr(profile?.state ?? detail?.state ?? "");
    setZip(profile?.zip ?? detail?.zip ?? "");
  }, [profile, detail, empno, derivedRoleId]);
  // What the pay fields start as — saving only writes pay when one changed
  // (saving a bio used to create an empty $0 pay row).
  const payStart = useMemo(() => ({
    payType: pay?.pay_type ?? "hourly",
    // Base rate defaults from ESC dbo.Employee.Rate (mirror) until a Pegasus pay row sets one (0 = not set).
    baseRate: pay?.base_rate != null && Number(pay.base_rate) > 0 ? String(pay.base_rate) : (detail?.pay_rate != null ? String(detail.pay_rate) : ""),
    burden: pay?.burden != null ? String(pay.burden) : "",
    rhpMult: String(pay?.rhp_multiplier ?? 1.75),
    phone: pay?.phone_stipend ? String(pay.phone_stipend) : "",
    commKind: pay?.commission_kind ?? "none",
    commRate: pay?.commission_rate != null ? String(pay.commission_rate) : "",
    effDate: pay?.effective_date ?? "",
    payNote: pay?.note ?? "",
  }), [pay, detail]);
  useEffect(() => {
    setPayType(payStart.payType);
    setBaseRate(payStart.baseRate);
    setBurden(payStart.burden);
    setRhpMult(payStart.rhpMult);
    setPhone(payStart.phone);
    setCommKind(payStart.commKind);
    setCommRate(payStart.commRate);
    setEffDate(payStart.effDate);
    setPayNote(payStart.payNote);
  }, [payStart, empno]);
  const payChanged =
    payType !== payStart.payType || baseRate !== payStart.baseRate || burden !== payStart.burden ||
    rhpMult !== payStart.rhpMult || phone !== payStart.phone || commKind !== payStart.commKind ||
    commRate !== payStart.commRate || effDate !== payStart.effDate || payNote !== payStart.payNote;
  useEffect(() => { setSaved(false); setErr(null); }, [empno]);

  const num = (s: string) => { const n = Number(s); return Number.isFinite(n) ? n : 0; };
  const costRate = num(baseRate) + num(burden);
  const rhp = payType === "hourly" && num(baseRate) > 0 && burden.trim() ? (num(baseRate) + num(burden)) * (num(rhpMult) || 1.75) : null;
  const roleName = (roles ?? []).find((r) => String(r.id) === roleId)?.name ?? null;

  async function save() {
    if (!empno) return;
    setSaving(true); setErr(null); setSaved(false);
    try {
      const { data: u } = await supabase.auth.getUser();
      const by = u.user?.email ?? "unknown";
      const now = new Date().toISOString();
      // Profile fields (bio/title/hire date) — anyone with team.edit.
      if (canEdit) {
        const ovr = (v: string, mirror: string | null | undefined) => {
          const t = v.trim();
          return !t || t === (mirror ?? "").trim() ? null : t;
        };
        const { error: pErr } = await supabase.from("tech_profile").upsert({
          empno, bio: bio.trim() || null, title: title.trim() || null,
          // Persist hire date / role only when they differ from ESC's value (else stay derived).
          hire_date: ovr(hireDate, detail?.hire_date),
          role_id: roleId === "" ? null : (derivedRoleId != null && Number(roleId) === derivedRoleId ? null : Number(roleId)),
          email: ovr(email, detail?.email),
          cellphone: ovr(cell, detail?.cellphone ?? detail?.phone),
          add1: ovr(add1, detail?.add1), city: ovr(city, detail?.city),
          state: ovr(stateAbbr, detail?.state), zip: ovr(zip, detail?.zip),
          updated_by: by, updated_at: now,
        });
        if (pErr) throw pErr;
      }
      // Pay + commission — only with team.pay, and only when a pay field changed.
      if (canPay && payChanged) {
        const { error: payErr } = await supabase.from("tech_pay").upsert({
          empno, pay_type: payType, base_rate: num(baseRate),
          burden: burden.trim() ? num(burden) : null,
          rhp_multiplier: num(rhpMult) || 1.75, phone_stipend: num(phone),
          commission_kind: commKind, commission_rate: num(commRate),
          effective_date: effDate || new Date().toISOString().slice(0, 10),
          note: payNote.trim() || null, updated_by: by, updated_at: now,
        });
        if (payErr) throw payErr;
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["tech_profile", empno] }),
        qc.invalidateQueries({ queryKey: ["tech_pay", empno] }),
        qc.invalidateQueries({ queryKey: ["tech_pay_table"] }),
        qc.invalidateQueries({ queryKey: ["tech_rates"] }),
        qc.invalidateQueries({ queryKey: ["scoreboard_productivity"] }),
        // The Team list reads role + title from tech_profile — refresh it so the
        // list reflects the save immediately (no manual refresh).
        qc.invalidateQueries({ queryKey: ["team_techs"] }),
      ]);
      setSaved(true);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setSaving(false);
    }
  }

  const schedDays = DAYS
    .map(([label, s, e]) => ({ label, s: fmtTime((detail?.[s] as string) ?? null), e: fmtTime((detail?.[e] as string) ?? null) }))
    .filter((d) => d.s && d.e);

  const commissionPreview =
    commKind === "service_invoice_pct" ? `${num(commRate)}% of each service invoice total`
    : commKind === "install_gp_pct" ? `${num(commRate)}% of install gross profit`
    : "No commission on this tech.";

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      size="lg"
      title={name}
      subtitle={empno ? `#${empno}${roleName ? ` · ${roleName}` : ""}${detail?.inactive ? " · inactive" : ""}` : undefined}
      footer={
        (canEdit || canPay) && empno ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-400">
              {err ? <span className="text-red-600">{err}</span>
                : saved ? <span className="inline-flex items-center gap-1 text-emerald-600"><Check className="h-3.5 w-3.5" /> Saved</span>
                : pay?.updated_at ? `Pay last updated ${new Date(pay.updated_at).toLocaleDateString()}${pay.updated_by ? ` by ${pay.updated_by}` : ""}`
                : null}
            </span>
            <button
              type="button" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        ) : undefined
      }
    >
      {empno && (
        <div className="space-y-7">
          {/* IDENTITY */}
          <PhotoIdentity empno={empno} name={name} title={title} setTitle={setTitle} bio={bio} setBio={setBio} canEdit={canEdit} />

          {/* EMPLOYMENT — role + editable contact (Pegasus overrides; ESC read-only) */}
          <Group label="Employment" hint="Role and contact edits are saved in Pegasus and ESC stays untouched; a blank field falls back to the ESC value. Active status and the schedule come from ESC.">
            <div className="mb-3">
              <Badge tone={detail?.inactive ? "slate" : "emerald"}>{detail?.inactive ? "Inactive" : "Active"}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Department / role" hint={profile?.role_id == null && derivedRoleId != null && detail?.dept ? `from ESC dept ${detail.dept}` : undefined}>
                <select value={roleId} disabled={!canEdit} onChange={(e) => setRoleId(e.target.value)}
                  className="h-9 w-full cursor-pointer rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50">
                  <option value="">— Select —</option>
                  {(roles ?? []).map((r) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                </select>
              </Field>
              <Field label="Hire date" hint={profile?.hire_date == null && detail?.hire_date ? "from ESC" : undefined}>
                <input type="date" value={hireDate} disabled={!canEdit} onChange={(e) => setHireDate(e.target.value)}
                  className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50" />
              </Field>
              <Field label="Email"><TextInput icon={Mail} value={email} onChange={setEmail} disabled={!canEdit} placeholder="name@email.com" /></Field>
              <Field label="Phone"><TextInput icon={Phone} value={cell} onChange={setCell} disabled={!canEdit} placeholder="612-555-0100" /></Field>
            </div>
            <div className="mt-3">
              <Field label="Address"><TextInput icon={MapPin} value={add1} onChange={setAdd1} disabled={!canEdit} placeholder="Street address" /></Field>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <Field label="City"><TextInput value={city} onChange={setCity} disabled={!canEdit} /></Field>
                <Field label="State"><TextInput value={stateAbbr} onChange={setStateAbbr} disabled={!canEdit} /></Field>
                <Field label="ZIP"><TextInput value={zip} onChange={setZip} disabled={!canEdit} /></Field>
              </div>
            </div>
            {schedDays.length > 0 && (
              <div className="mt-3">
                <span className="text-[11px] font-medium text-slate-400">Weekly schedule · from ESC</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {schedDays.map((d) => (
                    <span key={d.label} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{d.label} {d.s}–{d.e}</span>
                  ))}
                </div>
              </div>
            )}
          </Group>

          {/* PEGASUS LOGIN — associate this person's sign-in with their record
              (powers note stamping, "performed by", and the tech mobile app). */}
          {canManageLogins && (
            <Group label="Pegasus login" hint="Connect this team member to their sign-in. Their name then stamps the notes they write, the work they do, and (for techs) the mobile app.">
              <LoginLinkEditor empno={empno} memberName={name} memberEmail={detail?.email ?? null} />
            </Group>
          )}

          {/* SKILLS — editable: add/remove + Master/Journeyman level */}
          <Group label="Skills & certifications" hint={canEdit ? "What this tech is qualified for — feeds route recommendations. Saves on each change." : undefined}>
            <SkillsEditor empno={empno} canEdit={canEdit} />
          </Group>

          {/* PAY & COMMISSION (gated) */}
          <Group label="Pay & commission" hint={canPay ? "Private. The same record as Reports → Pay Rates — feeds job costing, the pay week and commission; never the customer invoice." : undefined}>
            {!canPay ? (
              <div className="flex items-center gap-2 rounded-lg border border-hairline bg-slate-50 px-3 py-3 text-sm text-slate-500">
                <Lock className="h-4 w-4 text-slate-400" />
                Restricted. Pay &amp; commission is visible only with the “View + edit pay” permission.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Pay type">
                    <Select value={payType} onChange={setPayType} options={PAY_TYPES} />
                  </Field>
                  <Field label={payType === "salary" ? "Annual salary" : "Base rate ($/hr)"} hint={pay?.base_rate == null && detail?.pay_rate != null ? "from ESC" : undefined}>
                    <MoneyInput value={baseRate} onChange={setBaseRate} />
                  </Field>
                  <Field label="Employer burden ($/hr)" hint="Taxes, comp, benefits per hour">
                    <MoneyInput value={burden} onChange={setBurden} />
                  </Field>
                  <Field label="Effective date">
                    <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)}
                      className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                  </Field>
                </div>
                {payType === "hourly" && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="RHP multiplier" hint="RHP = (base + burden) × this">
                      <NumInput value={rhpMult} onChange={setRhpMult} suffix="×" />
                    </Field>
                    <Field label="Phone stipend ($/week)">
                      <MoneyInput value={phone} onChange={setPhone} />
                    </Field>
                  </div>
                )}
                {payType === "hourly" && num(baseRate) > 0 && (
                  <p className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500">
                    <Info className="h-3.5 w-3.5 text-slate-400" />
                    <span>
                      Loaded labor cost <strong className="text-slate-700">{money(costRate)}/hr</strong> (job margins)
                      {rhp != null && <> · RHP <strong className="text-slate-700">{money(rhp)}/hr</strong> (pay week bonus + scoreboard)</>}
                    </span>
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Commission">
                    <Select value={commKind} onChange={setCommKind} options={COMMISSION_KINDS} />
                  </Field>
                  {commKind !== "none" && (
                    <Field label="Commission rate">
                      <NumInput value={commRate} onChange={setCommRate} suffix="%" />
                    </Field>
                  )}
                </div>
                {commKind !== "none" && (
                  <p className="rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-700">{commissionPreview}</p>
                )}
                <Field label="Note (optional)">
                  <input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="e.g. union scale, raise scheduled Jan"
                    className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                </Field>
              </div>
            )}
          </Group>
        </div>
      )}
    </SlideOver>
  );
}

// ---- Identity sub-block (photo upload lives here now) --------------------
function PhotoIdentity({ empno, name, title, setTitle, bio, setBio, canEdit }: {
  empno: string; name: string; title: string; setTitle: (v: string) => void; bio: string; setBio: (v: string) => void; canEdit: boolean;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const ext = file.type.includes("png") ? "png" : "jpg";
      const path = `${empno}.${ext}`;
      const { error: upErr } = await supabase.storage.from("tech-photos").upload(path, file, { upsert: true, contentType: file.type || "image/jpeg" });
      if (upErr) throw upErr;
      const pub = supabase.storage.from("tech-photos").getPublicUrl(path).data.publicUrl;
      const url = `${pub}?v=${Date.now()}`;
      const { error: dbErr } = await supabase.from("tech_photo").upsert({ empno, url, source: "manual", updated_at: new Date().toISOString() });
      if (dbErr) throw dbErr;
      await qc.invalidateQueries({ queryKey: ["tech_photos"] });
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center gap-1.5">
        <TechAvatar empno={empno} name={name} size="xl" />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-600 transition-colors hover:text-brand-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {busy ? "Uploading…" : "Photo"}
        </button>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <input value={title} disabled={!canEdit} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Service Technician)"
          className="w-full rounded-lg border border-hairline bg-white px-3 py-1.5 text-sm font-medium text-slate-800 placeholder:font-normal placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50" />
        <textarea value={bio} disabled={!canEdit} onChange={(e) => setBio(e.target.value)} rows={3}
          placeholder="Short bio — powers the “meet your tech” line in on-the-way texts. e.g. 12-year Sedgwick boiler specialist."
          className="w-full resize-none rounded-lg border border-hairline bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50" />
        {err && <p className="text-xs text-red-600">{err}</p>}
      </div>
    </div>
  );
}

// ---- Pegasus login link (app_user.empno ↔ this team member) --------------
// One login maps to one person. Setting it here is what makes Pegasus stamp the
// real name on notes/work and (for techs) drive the mobile app — instead of
// guessing a name from the email handle.
interface LoginRow { user_id: string; email: string | null; empno: string | null }
function useLoginAccounts() {
  return useQuery({
    queryKey: ["app_users_link"], staleTime: 30 * 1000,
    queryFn: async (): Promise<LoginRow[]> => {
      const { data, error } = await supabase.from("app_user").select("user_id, email, empno").order("email");
      if (error) throw error;
      return (data ?? []) as LoginRow[];
    },
  });
}

function useAppRoles() {
  return useQuery({
    queryKey: ["app_roles_pick"], staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ key: string; name: string }[]> => {
      const { data, error } = await supabase.from("app_role").select("key, name, is_admin").order("is_admin", { ascending: false }).order("key");
      if (error) throw error;
      return (data ?? []) as { key: string; name: string }[];
    },
  });
}

function LoginLinkEditor({ empno, memberEmail }: { empno: string; memberName: string; memberEmail: string | null }) {
  const qc = useQueryClient();
  const { data: logins } = useLoginAccounts();
  const { data: roles } = useAppRoles();
  const invitesLive = useOrgSettingJson<boolean>("team_invites_live", false).data ?? false;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState(memberEmail ?? "");
  const [newRole, setNewRole] = useState("tech");
  const [permOpen, setPermOpen] = useState(false);
  useEffect(() => { setNewEmail(memberEmail ?? ""); setMsg(null); setErr(null); }, [memberEmail, empno]);

  const all = logins ?? [];
  const linked = all.filter((l) => l.empno === empno);
  const available = all.filter((l) => !l.empno); // unlinked logins, free to attach
  const suggestion = memberEmail
    ? available.find((l) => (l.email ?? "").toLowerCase() === memberEmail.toLowerCase())
    : undefined;

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ["app_users_link"] }),
    qc.invalidateQueries({ queryKey: ["current_user_profile"] }), // restamp the signed-in name immediately
  ]);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null); setMsg(null);
    try { await fn(); await refresh(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };
  const stamp = async () => {
    const { data: u } = await supabase.auth.getUser();
    return { updated_by: u.user?.email ?? null, updated_at: new Date().toISOString() };
  };
  const link = (userId: string) => run(async () => {
    const s = await stamp();
    for (const o of linked.filter((l) => l.user_id !== userId)) {
      const { error } = await supabase.from("app_user").update({ empno: null, ...s }).eq("user_id", o.user_id);
      if (error) throw error;
    }
    const { error } = await supabase.from("app_user").update({ empno, ...s }).eq("user_id", userId);
    if (error) throw error;
  });
  const unlink = (userId: string) => run(async () => {
    const { error } = await supabase.from("app_user").update({ empno: null, ...(await stamp()) }).eq("user_id", userId);
    if (error) throw error;
  });

  // Provisioning via the gated edge function (create+invite / reset / resend).
  // While team_invites_live is off the buttons are disabled; the function also
  // refuses real sends, so nothing reaches a real person until rollout.
  const invoke = (action: "create_and_invite" | "send_reset" | "resend_invite", email: string) => run(async () => {
    const { data, error } = await supabase.functions.invoke("team-invite", { body: { action, email, empno, role_key: newRole } });
    if (error) throw error;
    if (data?.blocked) { setMsg("Invites are held until Pegasus is deployed — nothing sent."); return; }
    if (data?.error) throw new Error(data.detail || data.error);
    setMsg(action === "send_reset" ? `Password reset sent to ${email}.` : `Invite sent to ${email}.`);
  });

  return (
    <div className="space-y-3">
      {linked.length > 0 ? (
        <>
          <div className="space-y-1.5">
            {linked.map((l) => (
              <div key={l.user_id} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                <KeyRound className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{l.email ?? l.user_id}</span>
                <span className="shrink-0 text-[11px] font-medium text-emerald-700">Linked</span>
                <button type="button" onClick={() => unlink(l.user_id)} disabled={busy}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-slate-400 transition-colors hover:bg-white hover:text-red-500 disabled:opacity-50" title="Unlink this login">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlink className="h-3.5 w-3.5" />} Unlink
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={!invitesLive || busy} onClick={() => invoke("send_reset", linked[0].email ?? "")}
              title={invitesLive ? "Email a password-reset link" : "Held until rollout"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300">
              <RotateCcw className="h-3.5 w-3.5" /> Send password reset
            </button>
            <button type="button" disabled={!invitesLive || busy} onClick={() => invoke("resend_invite", linked[0].email ?? "")}
              title={invitesLive ? "Resend the set-password invite" : "Held until rollout"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300">
              <Send className="h-3.5 w-3.5" /> Resend invite
            </button>
            {!invitesLive && <span className="text-[11px] text-amber-700">held until rollout</span>}
            {/* SETTINGS-ACCESS-01: temporary passwords, two-factor resets and
                disabling live on the person's page in People & Access. */}
            <a href={`/settings?section=access&person=${encodeURIComponent(empno)}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline">
              Temporary password, two-factor reset, disable → People &amp; Access
            </a>
          </div>

          {/* Access & permissions — role baseline + per-user fine-tune + copy-from */}
          <div className="rounded-lg border border-hairline">
            <button type="button" onClick={() => setPermOpen((v) => !v)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-slate-50">
              <span className="flex items-center gap-2 text-xs font-semibold text-slate-700"><ShieldCheck className="h-3.5 w-3.5 text-slate-400" /> Access &amp; permissions</span>
              <ChevronDown className={cn("h-4 w-4 text-slate-400 transition-transform", permOpen && "rotate-180")} />
            </button>
            {permOpen && <div className="border-t border-hairline p-3"><UserPermissionsEditor userId={linked[0].user_id} /></div>}
          </div>
        </>
      ) : (
        <>
          {/* PRIMARY — create the login + email an invite to set a password */}
          <div className="rounded-lg border border-hairline bg-slate-50/60 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Create a login
              <InfoTip label="How the invite works">They get an email to set their own password; you never see or type it.</InfoTip>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-slate-500">Email</span>
                <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="name@sedgwickheating.com"
                  className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-slate-500">Access level</span>
                <select value={newRole} onChange={(e) => setNewRole(e.target.value)}
                  className="h-9 w-full cursor-pointer rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
                  {(roles ?? []).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                </select>
              </label>
            </div>
            <button type="button" disabled={!invitesLive || busy || !newEmail.trim()} onClick={() => invoke("create_and_invite", newEmail.trim().toLowerCase())}
              title={invitesLive ? "Create the account + email an invite" : "Held until rollout"}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Create login &amp; send invite
            </button>
            {!invitesLive && (
              <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700">
                <Lock className="mt-0.5 h-3 w-3 shrink-0" />
                <span>Invites are held until Pegasus is deployed. To hand someone a login today, use <a href={`/settings?section=access&person=${encodeURIComponent(empno)}`} className="font-semibold underline">People &amp; Access → Create with a temporary password</a>.</span>
              </p>
            )}
          </div>

          {/* SECONDARY — link an account that already signed in (rare under admin-provisioning) */}
          {available.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] font-medium text-slate-400">Or link an account that already signed in:</div>
              {suggestion && (
                <button type="button" onClick={() => link(suggestion.user_id)} disabled={busy}
                  className="flex w-full items-center gap-2 rounded-lg border border-dashed border-brand-300 bg-brand-50/50 px-3 py-2 text-left transition-colors hover:bg-brand-50 disabled:opacity-50">
                  <Sparkles className="h-4 w-4 shrink-0 text-brand-500" />
                  <span className="min-w-0 flex-1 text-xs text-slate-600">Matches this person&apos;s email — link <span className="font-semibold text-slate-800">{suggestion.email}</span></span>
                  <span className="shrink-0 text-[11px] font-semibold text-brand-700">Link</span>
                </button>
              )}
              {available.filter((l) => l.user_id !== suggestion?.user_id).length > 0 && (
                <select value="" disabled={busy} onChange={(e) => { if (e.target.value) link(e.target.value); }}
                  className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-600 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50">
                  <option value="">Link an existing login…</option>
                  {available.filter((l) => l.user_id !== suggestion?.user_id).map((l) => <option key={l.user_id} value={l.user_id}>{l.email ?? l.user_id}</option>)}
                </select>
              )}
            </div>
          )}
        </>
      )}

      {msg && <p className="text-xs font-medium text-emerald-600">{msg}</p>}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}

// ---- Skills editor (add / remove / set level, commits immediately) -------
function SkillsEditor({ empno, canEdit }: { empno: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const { data: assigned } = useTechSkills(empno);
  const { data: domains } = useSkillDomains();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const assignedList = assigned ?? [];
  const assignedIds = new Set(assignedList.map((s) => s.id));
  const unassigned = (domains ?? []).filter((d) => !assignedIds.has(d.id));

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ["tech_skills_profile", empno] }),
    qc.invalidateQueries({ queryKey: ["tech_skills"] }), // board route-rec map
    // Skills & Levels grid + the booking "Needs" row read these — keep them in step
    qc.invalidateQueries({ queryKey: ["tech_quals"] }),
    qc.invalidateQueries({ queryKey: ["skill_domains_all"] }),
  ]);
  const stamp = async () => {
    const { data: u } = await supabase.auth.getUser();
    return { last_admin_updated_at: new Date().toISOString(), last_admin_updated_by: u.user?.email ?? "unknown" };
  };
  const run = async (id: number, fn: () => Promise<{ error: unknown }>) => {
    setErr(null); setBusyId(id);
    try {
      const { error } = await fn();
      if (error) throw error;
      await refresh();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusyId(null); }
  };

  const add = (id: number) => run(id, async () =>
    supabase.from("tech_skill").upsert({ empno, skill_domain_id: id, admin_flagged_capable: true, ...(await stamp()) }, { onConflict: "empno,skill_domain_id" }));
  const remove = (id: number) => run(id, async () =>
    supabase.from("tech_skill").delete().eq("empno", empno).eq("skill_domain_id", id));
  const setLevel = (id: number, level: string) => run(id, async () =>
    supabase.from("tech_skill").update({ qualifier_override: level || null, ...(await stamp()) }).eq("empno", empno).eq("skill_domain_id", id));

  if (!canEdit) {
    return assignedList.length === 0 ? (
      <p className="text-sm text-slate-400">No skills flagged yet.</p>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {assignedList.map((s) => (
          <span key={s.id} className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-700">
            {s.name}
            {(s.qualifierOverride ?? s.domainQualifier) && <span className="rounded bg-brand-50 px-1 text-[10px] font-bold text-brand-700">{s.qualifierOverride ?? s.domainQualifier}</span>}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {assignedList.length === 0 ? (
        <p className="text-sm text-slate-400">No skills yet — add what this tech is qualified for below.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {assignedList.map((s) => (
            <span key={s.id} className={cn("inline-flex items-center gap-1 rounded-full border border-hairline bg-white py-1 pl-2.5 pr-1 text-xs font-medium text-slate-700", busyId === s.id && "opacity-50")}>
              {s.name}
              <select
                value={SKILL_LEVELS.includes(s.qualifierOverride ?? "") ? s.qualifierOverride! : ""}
                disabled={busyId === s.id}
                onChange={(e) => setLevel(s.id, e.target.value)}
                className="cursor-pointer rounded bg-slate-50 py-0.5 pl-1 text-[10px] font-semibold text-slate-500 focus:outline-none"
                title="Skill level"
              >
                <option value="">level</option>
                {SKILL_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <button type="button" onClick={() => remove(s.id)} disabled={busyId === s.id}
                className="flex h-4 w-4 items-center justify-center rounded-full text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500" title={`Remove ${s.name}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {unassigned.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-slate-400"><Plus className="h-3.5 w-3.5" /></span>
          <select value="" onChange={(e) => { if (e.target.value) add(Number(e.target.value)); }}
            className="h-8 rounded-lg border border-hairline bg-white px-2 text-xs text-slate-600 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
            <option value="">Add a skill…</option>
            {unassigned.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {busyId !== null && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-300" />}
        </div>
      )}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}

// ---- small presentational helpers ---------------------------------------
function Group({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
        {label}
        {hint && <InfoTip label={`About ${label}`}>{hint}</InfoTip>}
      </h4>
      <div className="mt-2">{children}</div>
    </section>
  );
}
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-slate-500">{label}{hint && <span className="font-normal text-slate-400"> · {hint}</span>}</span>
      {children}
    </label>
  );
}
function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full cursor-pointer rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
function MoneyInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
      <input type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-lg border border-hairline bg-white pl-6 pr-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
    </div>
  );
}
function NumInput({ value, onChange, suffix }: { value: string; onChange: (v: string) => void; suffix?: string }) {
  return (
    <div className="relative">
      <input type="number" min="0" step="0.1" value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
      {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{suffix}</span>}
    </div>
  );
}
function TextInput({ value, onChange, disabled, placeholder, icon: Icon }: {
  value: string; onChange: (v: string) => void; disabled?: boolean; placeholder?: string; icon?: typeof Mail;
}) {
  return (
    <div className="relative">
      {Icon && <Icon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-300" />}
      <input value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className={cn("h-9 w-full rounded-lg border border-hairline bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50", Icon ? "pl-8 pr-3" : "px-3")} />
    </div>
  );
}
function Badge({ tone, children }: { tone: "emerald" | "slate"; children: React.ReactNode }) {
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium",
      tone === "emerald" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600")}>{children}</span>
  );
}
